import { inject, Injectable } from '@angular/core';
import { AuthQuery } from 'app/core/auth/auth.query';
import { AutopropBackend } from 'app/shared/backend';
import { FeatureBundleDetailModalComponent } from 'app/shared/feature-bundle/feature-bundle-detail-modal.component';
import { OrderSummaryModalComponent } from 'app/shared/feature-bundle/order-summary-modal.component';
import { OrderSummaryBasicSubscriptionModalComponent } from 'app/shared/order-summary-basic-subscription/order-summary-basic-subscription-modal.component';
import { OrderSummaryProductModalComponent } from 'app/shared/order-summary-product/order-summary-product-modal.component';
import { EventsService } from 'app/shared/service/events.service';
import { MyAutopropApi } from 'app/shared/service/my-autoprop-api.service';
import { FeatureBundle, Product, Subscription } from 'app/shared/service/subscriptions.model';
import { ModalsService } from 'app/shared/ui/modals/modals.service';
import { loadScript } from 'app/shared/utils';
import { environment } from 'environments/environment';
import { EMPTY } from 'rxjs';
import { map, mergeMap, take } from 'rxjs/operators';
import { BillingHistoryModalComponent } from './billing-history-modal.component';
import { ViewDetailsSubscriptionsModalComponent } from './view-details-subscriptions-modal.component';


@Injectable({
    providedIn: 'root',
})
export class BillingService {

    boardName: string | null = null;

    chosenBillingPeriod: 'monthly' | 'yearly' = 'yearly';

    userBillingPeriod: null | 'monthly' | 'yearly';

    nextBill: null | Subscription = null;

    features = ['Core features'];

    private backend = inject(AutopropBackend);

    private es = inject(EventsService);

    private myAutopropApi = inject(MyAutopropApi);

    private authQuery = inject(AuthQuery);

    private modalsService = inject(ModalsService);

    constructor() {
        loadScript('https://checkout.stripe.com/checkout.js');
        this.myAutopropApi.getBillingDetails().subscribe(billing => {
            this.es.billingDetails.next(billing.payload.billingDetails);
            this.boardName = billing.payload.boardName;
        });
    }

    getProduct(key: string, source: string) {
        return this.backend.get(`/api/user/product/${key}?source=${source}`).pipe(map(response => response.payload));
    }

    bundleActive(bundleId: string) {
        return !!this.es.billingDetails.value?.subscriptions.find(s => s.planId === bundleId);
    }

    openBundleDetail(featureBundle: FeatureBundle) {
        const inputs = {
            featureBundle: featureBundle,
            chosenBillingPeriod: this.chosenBillingPeriod,
            userBillingPeriod: this.userBillingPeriod,
            active: this.bundleActive(featureBundle.bundleId.monthly) || this.bundleActive(featureBundle.bundleId.yearly),
            boardName: this.boardName,
        };
        return this.modalsService.openModal(FeatureBundleDetailModalComponent, inputs).result.pipe(
            mergeMap((result: any) => {
                if (result.action === 'subscribe') {
                    return this.openOrderSummary(result.featureBundle, result.chosenBillingPeriod);
                }
                return EMPTY;
            })
        );
    }

    openOrderSummary(featureBundle: FeatureBundle, billingPeriod: 'monthly' | 'yearly') {
        return this.authQuery.getUserInfo$().pipe(
            take(1),
            map(user => {
                const inputs = {
                    featureBundle,
                    billingPeriod,
                    customerId: this.es.billingDetails.value?.customerId,
                    savedCard: this.es.billingDetails.value?.savedCard,
                    userEmail: user.username,
                };
                return this.modalsService.openModal(OrderSummaryModalComponent, inputs);
            })
        );
    }

    openOrderSummaryProduct(product: Product, metadata: Record<string, string> | undefined = undefined, callbacks: { onPaymentSubmit?: () => boolean; onPaymentSuccess?: (response: any) => void; onPaymentError?: (error: unknown) => void; onPaymentCancel?: () => void } = {}) {
        const inputs = {
            product,
            purchaseMetadata: metadata,
            customerId: this.es.billingDetails.value?.customerId,
            savedCard: this.es.billingDetails.value?.savedCard,
            paymentSubmit: callbacks.onPaymentSubmit,
            paymentError: callbacks.onPaymentError,
            paymentCancel: callbacks.onPaymentCancel,
            paymentSuccess: callbacks.onPaymentSuccess,
        };
        return this.modalsService.openModal(OrderSummaryProductModalComponent, inputs);
    }

    openOrderSummaryBasicSubscription(stripeId: string, name: string, price: number) {
        const inputs = {
            name: name,
            price: price,
            stripeId: stripeId,
            customerId: this.es.billingDetails.value?.customerId,
            savedCard: this.es.billingDetails.value?.savedCard,
        };
        return this.modalsService.openModal(OrderSummaryBasicSubscriptionModalComponent, inputs);
    }

    openUpdateCreditCard() {
        return this.authQuery.getUserInfo$().pipe(
            take(1),
            map(user => {
                const handler = (window as any).StripeCheckout.configure({
                    key: environment.stripeApiKey,
                    locale: 'auto',
                    token: (token: any) => {
                        this.myAutopropApi.updateCreditCard(token.id).subscribe(billingDetails => {
                            this.es.billingDetails.next(billingDetails);
                        });
                    },
                });

                handler.open({
                    name: 'AUTOPROP',
                    description: 'Update Card Details',
                    panelLabel: 'Update Card Details',
                    email: user.username,
                    image: '/assets/images/logo-small.svg',
                });
                return;
            })
        );
    }

    openBillingHistory() {
        return this.modalsService.openModal(BillingHistoryModalComponent, { invoices: this.es.billingDetails.value?.history });
    }

    openViewDetailsSubscriptions(products: FeatureBundle[], subscriptions: Subscription[]) {
        const inputs = {
            subscriptions,
            products,
        };
        return this.modalsService.openModal(ViewDetailsSubscriptionsModalComponent, inputs);
    }

    checkCouponCode(code: string) {
        return this.myAutopropApi.checkCouponCode(code);
    }
}
